import React, { useState, useRef, useEffect } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { BsMic } from "react-icons/bs";
import { AiOutlineStop } from "react-icons/ai";
import { ProgressSpinner } from "primereact/progressspinner";
import ReactMarkdown from "react-markdown";
import { Node, Edge } from "reactflow";
import { NodeData } from "../types/types";
import { useStore } from "../App/store";
import { buildSystemPromptWithContext } from "../utils/aiContextBuilder";
import {
  parseAIStructureResponse,
  parseStructuredAIResponse,
  calculateChildNodePositions,
  AIStructureResponse,
} from "../utils/aiResponseParser";
import "./AIInputFloat.css";

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface AIInputFloatProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (text: string) => void;
  nodes: Node<NodeData>[];
  edges: Edge[];
  selectedNodeId: string | null;
  onUpdateNodes?: (
    nodes: Node<NodeData>[],
    edges: Edge[],
    deleteNodeIds?: string[],
    selectNodeId?: string | null,
    formatAll?: boolean
  ) => void;
  aiModel?: string;
}

const AIInputFloat: React.FC<AIInputFloatProps> = ({
  visible,
  onClose,
  onSubmit,
  nodes,
  edges,
  selectedNodeId,
  onUpdateNodes,
  aiModel = "gpt-4o-mini",
}) => {
  const [inputText, setInputText] = useState("");
  const [chat, setChat] = useState<{ role: "user" | "ai"; text: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const stopStreamingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Backend API base URL
  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.MODE === "development"
      ? "http://127.0.0.1:5001/focusmap-development/us-central1/api"
      : import.meta.env.MODE === "dev"
      ? "https://api-se7ho5yqsa-uc.a.run.app"
      : "https://api-deqxfy6vga-uc.a.run.app");

  useEffect(() => {
    if (visible) {
      // Clear chat history and input when showing the float
      setChat([]);
      setInputText("");
      setLoading(false);
      setRecording(false);
      stopStreamingRef.current = false;

      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [visible]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [chat, loading]);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = "da-DK"; // Danish language
      recognitionRef.current.continuous = true; // Keep listening for longer
      recognitionRef.current.interimResults = true; // Show interim results
      recognitionRef.current.maxAlternatives = 1;

      let finalTranscript = "";
      let silenceTimer: NodeJS.Timeout | null = null;

      recognitionRef.current.onresult = (event: any) => {
        let interimTranscript = "";

        // Process all results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
            console.log("Final speech result:", transcript);
          } else {
            interimTranscript += transcript;
            console.log("Interim speech result:", transcript);
          }
        }

        // Update input with current transcript
        const currentText = (finalTranscript + interimTranscript).trim();
        if (currentText) {
          setInputText(currentText);
        }

        // Reset silence timer on new speech
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }

        // Wait for 2 seconds of silence before auto-submitting
        silenceTimer = setTimeout(() => {
          if (finalTranscript.trim() && !loading) {
            console.log(
              "Auto-submitting after silence:",
              finalTranscript.trim()
            );
            const textToSubmit = finalTranscript.trim();
            finalTranscript = "";
            setInputText("");
            handleSendVoice(textToSubmit);
            // Note: Don't stop recognition, keep mic open
          }
        }, 2000); // Wait 2 seconds of silence
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        if (event.error === "no-speech") {
          console.log("No speech detected");
        } else if (event.error === "not-allowed") {
          alert(
            "Microphone adgang nægtet. Tillad mikrofonadgang i browserindstillingerne."
          );
        }
        setRecording(false);
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }
      };

      recognitionRef.current.onend = () => {
        console.log("Speech recognition ended");
        // Only set recording to false if user manually stopped it
        // Otherwise, restart automatically to keep mic open
        if (recognitionRef.current && recording) {
          console.log("Restarting speech recognition to keep mic open");
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.error("Failed to restart recognition:", e);
            setRecording(false);
          }
        } else {
          setRecording(false);
        }
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }
      };

      recognitionRef.current.onstart = () => {
        console.log("Speech recognition started");
      };
    } else {
      console.warn("Speech recognition not supported in this browser");
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Allow dragging from the drag handle or the container margins/borders
    // Prevent dragging if user is clicking on interactive elements
    const target = e.target as HTMLElement;

    // Don't start drag if clicking on interactive elements
    if (
      target.tagName === "TEXTAREA" ||
      target.tagName === "BUTTON" ||
      target.tagName === "INPUT" ||
      target.closest(".ai-input-wrapper") ||
      target.closest(".ai-input-icon") ||
      target.closest(".ai-chat-history")
    ) {
      return;
    }

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  // Toggle voice recording
  const toggleRecording = () => {
    if (!recognitionRef.current) {
      console.warn("Speech recognition not supported");
      return;
    }

    if (recording) {
      recognitionRef.current.stop();
      setRecording(false);
    } else {
      recognitionRef.current.start();
      setRecording(true);
    }
  };

  // Helper function to estimate request complexity and determine max_tokens
  const estimateMaxTokens = (message: string, nodeCount: number): number => {
    // Extract all numbers from the message and look for multiplication patterns
    const numbers = message.match(/\b(\d+)\b/g);
    let requestedNodes = 3; // Default

    if (numbers && numbers.length > 0) {
      const nums = numbers.map((n) => parseInt(n));

      // Look for patterns like "3 nodes + 3 subnodes" or "3 med 3 hver"
      if (nums.length >= 2) {
        // If we see multiple numbers, multiply them (e.g., 3 nodes with 3 each = 9 + 3 = 12)
        requestedNodes = nums[0] * (1 + nums[1]);
      } else {
        requestedNodes = nums[0];
      }

      // Look for keywords that suggest more nodes
      const lowerMsg = message.toLowerCase();
      if (
        lowerMsg.includes("sub") ||
        lowerMsg.includes("each") ||
        lowerMsg.includes("hver")
      ) {
        // Double the estimate if subnodes are mentioned
        requestedNodes = requestedNodes * 1.5;
      }
    }

    console.log(`Estimated ${requestedNodes} nodes from request: "${message}"`);

    // Estimate tokens needed per node (including structure)
    const tokensPerNode = 180; // Increased from 150
    const baseTokens = 1500; // Increased base for conversational response
    const estimated = baseTokens + Math.ceil(requestedNodes) * tokensPerNode;

    // Cap based on model capabilities
    const isClaudeModel = aiModel.startsWith("claude");
    const maxAllowed = isClaudeModel ? 8000 : 16000;

    const finalTokens = Math.min(estimated, maxAllowed);
    console.log(
      `Allocating ${finalTokens} tokens (estimated ${estimated}, max ${maxAllowed})`
    );

    return finalTokens;
  };

  // Helper function to detect if request is too large and needs batching
  const shouldBatchRequest = (
    message: string
  ): { shouldBatch: boolean; nodeCount: number } => {
    const numbers = message.match(/\b(\d+)\b/g);
    let requestedNodes = 0;

    if (numbers && numbers.length > 0) {
      const nums = numbers.map((n) => parseInt(n));

      // Look for patterns like "3 nodes + 3 subnodes" or "3 med 3 hver"
      if (nums.length >= 2) {
        requestedNodes = nums[0] * (1 + nums[1]);
      } else {
        requestedNodes = nums[0];
      }

      const lowerMsg = message.toLowerCase();
      if (
        lowerMsg.includes("sub") ||
        lowerMsg.includes("each") ||
        lowerMsg.includes("hver")
      ) {
        requestedNodes = Math.ceil(requestedNodes * 1.5);
      }
    }

    // SAFETY: If calculation results in absurd numbers (>100), it's likely an error
    // The user probably pasted content, not requesting thousands of nodes
    const MAX_REASONABLE_NODES = 25;
    if (requestedNodes > MAX_REASONABLE_NODES) {
      console.warn(
        `Detected unrealistic node count (${requestedNodes}). Treating as content to structure, not a quantity request.`
      );
      return { shouldBatch: false, nodeCount: 0 }; // Let AI handle it normally
    }

    // Batch if requesting more than 15 nodes at once
    return { shouldBatch: requestedNodes > 15, nodeCount: requestedNodes };
  };

  // Non-streamed OpenAI call with structured outputs (guaranteed valid JSON)
  const askOpenAIStructured = async (
    text: string,
    chatHistory: Array<{ role: string; content: string }>,
    maxTokens?: number
  ): Promise<AIStructureResponse> => {
    try {
      // Build context-aware system prompt
      const systemPrompt = buildSystemPromptWithContext(
        nodes,
        edges,
        selectedNodeId,
        chatHistory
      );

      // Calculate dynamic max_tokens if not provided
      const dynamicMaxTokens =
        maxTokens || estimateMaxTokens(text, nodes.length);

      console.log("🤖 AI Request Details (Structured Output):");
      console.log("  Model:", aiModel);
      console.log("  Max Tokens:", dynamicMaxTokens);
      console.log("  Message:", text);
      console.log("  API URL:", `${API_BASE_URL}/brainstorm`);

      const response = await fetch(`${API_BASE_URL}/brainstorm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: text,
          systemPrompt,
          chatHistory,
          stream: false, // No streaming for structured outputs
          model: aiModel,
          maxTokens: dynamicMaxTokens,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`
        );
      }

      const data: AIStructureResponse = await response.json();
      console.log("✅ Received valid structured JSON from API");
      return data;
    } catch (error) {
      console.error("Error in askOpenAIStructured:", error);
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        console.error("❌ Cannot reach API endpoint. Possible causes:");
        console.error("  - Firebase Functions not deployed");
        console.error("  - Local emulator not running");
        console.error("  - Network/CORS issue");
        console.error("  - API URL:", `${API_BASE_URL}/brainstorm`);
      }
      throw error;
    }
  };

  // Streamed OpenAI call via backend
  const askOpenAI = async (
    text: string,
    onChunk: (chunk: string) => void,
    chatHistory: Array<{ role: string; content: string }>,
    maxTokens?: number
  ): Promise<void> => {
    try {
      setStreaming(true);

      // Build context-aware system prompt
      const systemPrompt = buildSystemPromptWithContext(
        nodes,
        edges,
        selectedNodeId,
        chatHistory
      );

      // Calculate dynamic max_tokens if not provided
      const dynamicMaxTokens =
        maxTokens || estimateMaxTokens(text, nodes.length);

      console.log("🤖 AI Request Details:");
      console.log("  Model:", aiModel);
      console.log("  Max Tokens:", dynamicMaxTokens);
      console.log("  Message:", text);

      const response = await fetch(`${API_BASE_URL}/brainstorm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: text,
          systemPrompt,
          chatHistory,
          stream: true,
          model: aiModel,
          maxTokens: dynamicMaxTokens,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("No response body");
      }

      while (true) {
        if (stopStreamingRef.current) {
          await reader.cancel();
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content || "";
              if (content) {
                onChunk(content);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error("Error in askOpenAI:", error);
      onChunk("\n[Error: " + ((error as any).message || "Unknown error") + "]");
    } finally {
      setStreaming(false);
    }
  };

  // Handle sending question with structured outputs (no streaming)
  const handleSend = async () => {
    const question = inputText.trim();
    if (!question) return;

    setChat((prev) => [...prev, { role: "user", text: question }]);
    setInputText("");
    setLoading(true);

    try {
      const chatHistory = chat.map((msg) => ({
        role: msg.role === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      console.log("=== AIInputFloat: handleSend (Structured Output) ===");
      console.log("Selected node ID:", selectedNodeId);
      console.log("Current nodes count:", nodes.length);

      // Call API with structured outputs enabled
      const structuredResponse = await askOpenAIStructured(
        question,
        chatHistory
      );

      // Parse the guaranteed-valid JSON response
      const parsedResponse = parseStructuredAIResponse(
        structuredResponse,
        nodes
      );

      console.log("Parsed structured response:", {
        nodesCount: parsedResponse.nodes.length,
        edgesCount: parsedResponse.edges.length,
        parentNodeId: parsedResponse.parentNodeId,
        deleteNodeIds: parsedResponse.deleteNodeIds?.length || 0,
        errors: parsedResponse.errors,
        isValid: parsedResponse.isValid,
      });

      // Update chat with the conversational response
      setChat((prev) => [
        ...prev,
        {
          role: "ai",
          text:
            parsedResponse.conversationalResponse ||
            "Response received and nodes added.",
        },
      ]);

      // Process nodes or deletions
      if (
        parsedResponse.nodes.length > 0 ||
        parsedResponse.deleteNodeIds.length > 0
      ) {
        console.log("Processing nodes/edges/deletions from AI response...");

        // Update nodes and edges via callback
        if (onUpdateNodes) {
          onUpdateNodes(
            parsedResponse.nodes,
            parsedResponse.edges,
            parsedResponse.deleteNodeIds,
            parsedResponse.selectNodeId,
            parsedResponse.formatAll
          );
        }

        console.log("✅ Nodes updated successfully");
      } else if (parsedResponse.errors.length > 0) {
        console.error("❌ Errors in AI response:", parsedResponse.errors);
        setChat((prev) => [
          ...prev,
          {
            role: "ai",
            text: `Error processing response: ${parsedResponse.errors.join(
              ", "
            )}`,
          },
        ]);
      } else {
        console.log("No nodes to add or delete");
      }
    } catch (error) {
      console.error("Error in handleSend:", error);
      setChat((prev) => [
        ...prev,
        {
          role: "ai",
          text: `Error: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        },
      ]);
    } finally {
      setLoading(false);
      console.log("=== End AIInputFloat: handleSend ===");
      console.log("AI Input submitted:", question);

      // Log error to Firestore for debugging
      if (chat[chat.length - 1]?.text.includes("Error")) {
        try {
          const errorLog = {
            timestamp: new Date().toISOString(),
            error: chat[chat.length - 1].text,
            question,
            model: aiModel,
            nodesCount: nodes.length,
          };
          console.log("Error logged to Firestore");
          // TODO: Add actual Firestore logging here if needed
        } catch (logError) {
          console.error("Failed to log error:", logError);
        }
      }

      onSubmit(question);
    }
  };

  // Handle sending voice question (can keep streaming for voice)
  const handleSendVoice = async (voiceText: string) => {
    setChat((prev) => [...prev, { role: "user", text: voiceText }]);
    setLoading(true);
    stopStreamingRef.current = false;

    let aiResponse = "";
    setChat((prev) => [...prev, { role: "ai", text: "" }]);

    // Build chat history
    const chatHistory = chat.map((msg) => ({
      role: msg.role === "user" ? "user" : "assistant",
      content: msg.text,
    }));

    await askOpenAI(
      voiceText,
      (chunk) => {
        aiResponse += chunk;

        // Try to extract and stream just the conversational part
        const responseMatch = aiResponse.match(
          /"response":\s*"([^"]*(?:\\.[^"]*)*)"/s
        );
        if (responseMatch) {
          const conversationalText = responseMatch[1]
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"');
          setChat((prev) => {
            const newChat = [...prev];
            newChat[newChat.length - 1] = {
              role: "ai",
              text: conversationalText,
            };
            return newChat;
          });
        } else if (!aiResponse.includes("{")) {
          setChat((prev) => {
            const newChat = [...prev];
            newChat[newChat.length - 1] = { role: "ai", text: aiResponse };
            return newChat;
          });
        }
      },
      chatHistory
    );

    // Parse AI response and extract structure (after streaming is complete)
    console.log("=== AIInputFloat: handleSend ===");
    console.log(
      "About to parse AI response, aiResponse length:",
      aiResponse.length
    );
    console.log("Selected node ID:", selectedNodeId);
    console.log("Current nodes count:", nodes.length);
    const parsedResponse = parseAIStructureResponse(aiResponse, nodes);
    console.log("Parsed response:", {
      nodesCount: parsedResponse.nodes.length,
      edgesCount: parsedResponse.edges.length,
      parentNodeId: parsedResponse.parentNodeId,
      deleteNodeIds: parsedResponse.deleteNodeIds?.length || 0,
      errors: parsedResponse.errors,
      isValid: parsedResponse.isValid,
    });

    // Update chat with the final conversational response
    setChat((prev) => {
      const newChat = [...prev];
      newChat[newChat.length - 1] = {
        role: "ai",
        text:
          parsedResponse.conversationalResponse ||
          "Response received and nodes added.",
      };
      return newChat;
    });

    // Process nodes or deletions
    const hasNodesToAdd = parsedResponse.nodes.length > 0;
    const hasDeletions = (parsedResponse.deleteNodeIds?.length || 0) > 0;

    if (hasNodesToAdd || hasDeletions) {
      let nodesWithPositions: Node<NodeData>[] = [];

      if (hasNodesToAdd) {
        console.log("Processing nodes for addition...");

        // Determine which nodes are new vs existing
        const existingNodeIds = new Set(nodes.map((n) => n.id));
        const newNodes = parsedResponse.nodes.filter(
          (n) => !existingNodeIds.has(n.id)
        );
        const existingNodesUpdates = parsedResponse.nodes.filter((n) =>
          existingNodeIds.has(n.id)
        );

        console.log(
          `Found ${newNodes.length} new nodes and ${existingNodesUpdates.length} node updates`
        );

        // Calculate proper positions ONLY for new nodes
        // Check if one of the new nodes is a center node
        const newCenterNode = newNodes.find((n) => n.type === "center");

        // Find existing center node as fallback parent
        const existingCenterNode = nodes.find((n) => n.type === "center");
        const defaultParentId =
          parsedResponse.parentNodeId ||
          selectedNodeId ||
          existingCenterNode?.id ||
          "root";

        let nodesWithCalculatedPositions: Node<NodeData>[] = [];

        if (newNodes.length > 0) {
          // Get the child node distance from store (adjustable with Cmd+Plus/Minus)
          const childNodeDistance = useStore.getState().childNodeDistance;

          // Separate center node from other nodes
          const centerNodes = newNodes.filter((n) => n.type === "center");
          const regularNodes = newNodes.filter((n) => n.type !== "center");

          console.log(
            `Found ${centerNodes.length} center nodes and ${regularNodes.length} regular nodes`
          );

          // For each regular node, determine its correct parentId
          nodesWithCalculatedPositions = regularNodes.map((node) => {
            // Check if this node's parentId points to the new center node
            let nodeParentId = node.parentId || defaultParentId;

            if (newCenterNode && node.parentId === newCenterNode.id) {
              // This node should connect to the new center node
              console.log(
                `Node ${node.data.label} connects to new center node ${newCenterNode.id}`
              );
              nodeParentId = newCenterNode.id;
            } else if (!node.parentId) {
              // No explicit parentId, use default
              nodeParentId = defaultParentId;
            }

            return {
              ...node,
              parentId: nodeParentId,
            };
          });

          // Add center nodes without parentId
          const centerNodesWithoutParent = centerNodes.map((node) => {
            const { parentId, ...nodeWithoutParent } = node;
            return nodeWithoutParent as Node<NodeData>;
          });

          // Calculate positions for all nodes
          const allNewNodes = [
            ...centerNodesWithoutParent,
            ...nodesWithCalculatedPositions,
          ];

          // Group nodes by their parent for position calculation
          const nodesByParent = new Map<string, Node<NodeData>[]>();
          allNewNodes.forEach((node) => {
            const parent = node.parentId || "center";
            if (!nodesByParent.has(parent)) {
              nodesByParent.set(parent, []);
            }
            nodesByParent.get(parent)!.push(node);
          });

          // Calculate positions for each group
          const nodesWithPositions: Node<NodeData>[] = [];
          nodesByParent.forEach((nodeGroup, parent) => {
            const positions = calculateChildNodePositions(
              nodes,
              parent === "center" ? existingCenterNode?.id || "root" : parent,
              nodeGroup.length,
              childNodeDistance
            );

            nodeGroup.forEach((node, i) => {
              nodesWithPositions.push({
                ...node,
                position: positions[i] || node.position || { x: 0, y: 0 },
              });
            });
          });

          nodesWithCalculatedPositions = nodesWithPositions;
          console.log(
            "Calculated positions for new nodes:",
            nodesWithCalculatedPositions
          );
        }

        // For existing nodes, check if parentId is changing (move) or not (style update)
        // Only keep parentId if it's different from the current parentId (indicating a move)
        const cleanedExistingUpdates = existingNodesUpdates.map((node) => {
          const existingNode = nodes.find((n) => n.id === node.id);
          if (!existingNode) return node;

          // If parentId is changing, keep it (this is a move operation)
          if (node.parentId && node.parentId !== existingNode.parentId) {
            console.log(
              `Move detected: ${node.data.label} from ${existingNode.parentId} to ${node.parentId}`
            );
            return node; // Keep the new parentId for move operations
          }

          // If parentId is the same or undefined, remove it (style update)
          const { parentId, ...nodeWithoutParent } = node;
          return nodeWithoutParent as Node<NodeData>;
        });

        nodesWithPositions = [
          ...nodesWithCalculatedPositions,
          ...cleanedExistingUpdates,
        ];

        console.log(
          "Nodes with positions:",
          nodesWithPositions.map((n) => ({
            id: n.id,
            label: n.data.label,
            parentId: n.parentId,
            isUpdate: existingNodeIds.has(n.id),
          }))
        );
      }

      if (hasDeletions) {
        console.log("Processing deletions:", parsedResponse.deleteNodeIds);
      }

      // Notify parent component to add/move/delete nodes
      if (onUpdateNodes) {
        console.log(
          "Calling onUpdateNodes with",
          nodesWithPositions.length,
          "nodes,",
          parsedResponse.edges.length,
          "edges,",
          parsedResponse.deleteNodeIds?.length || 0,
          "deletions, and selectNodeId:",
          parsedResponse.selectNodeId || "none"
        );
        onUpdateNodes(
          nodesWithPositions,
          parsedResponse.edges,
          parsedResponse.deleteNodeIds || [],
          parsedResponse.selectNodeId || null,
          parsedResponse.formatAll || false
        );
      } else {
        console.warn("onUpdateNodes callback is not defined!");
      }
    } else {
      console.log("No nodes to add or delete");
    }
    console.log("=== End AIInputFloat: handleSend ===");

    setLoading(false);
    onSubmit(voiceText); // Notify parent if needed
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() && !loading) {
        handleSend();
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="ai-input-float"
        ref={containerRef}
        style={{
          left: position.x ? `${position.x}px` : "50%",
          top: position.y ? `${position.y}px` : "auto",
          transform: position.x ? "none" : "translateX(-50%)",
        }}
        onContextMenu={(e) => {
          // Stop propagation to prevent flow context menu
          e.stopPropagation();
          // Allow default browser context menu
        }}
      >
        <div className="ai-input-container" onMouseDown={handleMouseDown}>
          <div className="ai-input-drag-handle" onMouseDown={handleMouseDown}>
            <div className="ai-input-drag-indicator" />
          </div>

          {/* Chat history */}
          {chat.length > 0 && (
            <div className="ai-chat-history" ref={chatAreaRef}>
              {chat.map((msg, idx) => (
                <div
                  key={idx}
                  className={`ai-chat-message ai-chat-${msg.role}`}
                >
                  <div className="ai-chat-role">
                    {msg.role === "user" ? "You" : "AI"}
                  </div>
                  <div className="ai-chat-text">
                    {msg.role === "ai" ? (
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="ai-loading">
                  <ProgressSpinner
                    style={{ width: "20px", height: "20px" }}
                    strokeWidth="4"
                  />
                </div>
              )}
            </div>
          )}

          <div className="ai-input-wrapper">
            <InputTextarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What do you want to map? (experimental)"
              rows={3}
              autoResize
              className="ai-input-textarea"
              disabled={loading}
            />
            <div
              className={`ai-input-icon ${recording ? "recording" : ""}`}
              onClick={toggleRecording}
              style={{ cursor: "pointer" }}
              title={recording ? "Stop recording" : "Start voice input"}
            >
              <BsMic size={24} color="#ffffff" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIInputFloat;
